import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  ArrowRight,
  Zap,
  ChevronRight,
  Shield,
  Target,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "../components/ui/button";

const Services = () => {
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load data from the expanded local mock file
    import("../data/mock").then((module) => {
      const servicesArray = Object.values(module.services);

      const servicesWithIcons = servicesArray.map((service) => ({
        ...service,
        // Safely map the string name to the Lucide component
        icon:
          LucideIcons[service.icon] ||
          LucideIcons[service.icon_name] ||
          LucideIcons.Briefcase,
      }));

      setServices(servicesWithIcons);
      setLoading(false);
    });
  }, []);

  // FIXED SCROLLING LOGIC
  useEffect(() => {
    // IMPORTANT: Do not attempt to scroll if the page is still rendering the loader
    if (loading) return;

    if (location.hash) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);

      if (element) {
        // Slight delay ensures the DOM has painted the components before scrolling
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location, loading]); // <-- Added 'loading' to the dependency array

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="h-16 w-16 border-4 border-maroon-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium tracking-wide uppercase text-sm">
            Loading Portfolio...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-white">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.02] pointer-events-none">
          <svg width="100%" height="100%">
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-5 py-2 mb-6 text-xs font-black tracking-widest text-maroon-700 uppercase bg-maroon-50 rounded-full"
          >
            Maroons Business Solutions
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 mb-8"
          >
            Consulting & <span className="text-maroon-700">Technology</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto text-xl text-slate-600 leading-relaxed"
          >
            Comprehensive solutions designed to engineer resilient foundations,
            scale operations, and drive measurable commercial growth.
          </motion.p>
        </div>
      </section>

      {/* --- INTRODUCTORY BLOCK --- */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-[2rem] overflow-hidden shadow-2xl order-2 lg:order-1 group"
            >
              <img
                src="https://images.unsplash.com/photo-1757405944970-dfca42373629"
                alt="Professional Business Meeting"
                className="w-full h-[450px] object-cover transform group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="w-12 h-1 bg-maroon-500 mb-4 rounded-full"></div>
                <h3 className="text-3xl font-bold mb-2">
                  Professional Excellence
                </h3>
                <p className="text-white/90 text-lg">
                  Delivering world-class solutions for your business success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Comprehensive <br />
                <span className="text-maroon-700">Business Solutions</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                From strategic planning to execution, we cover all aspects of
                your business needs under one roof. Our integrated approach
                ensures that strategy, technology, finance, and people work in
                perfect harmony.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg text-slate-700 font-medium">
                  <Target size={18} className="text-maroon-700" /> Measurable
                  Outcomes
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-lg text-slate-700 font-medium">
                  <Shield size={18} className="text-maroon-700" /> End-to-End
                  Execution
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- DETAILED SERVICES LIST (STICKY SCROLL LAYOUT) --- */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto space-y-40">
          {Array.isArray(services) &&
            services.map((service, idx) => (
              <div
                key={service.id}
                id={service.id}
                className="scroll-mt-32 relative"
              >
                <div
                  className={`flex flex-col lg:flex-row gap-16 lg:gap-24 ${idx % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}
                >
                  {/* STICKY VISUAL COLUMN */}
                  <div className="w-full lg:w-1/3">
                    <div className="sticky top-32 space-y-8">
                      {/* Main Icon Card */}
                      <div className="relative aspect-square rounded-[2.5rem] bg-gradient-to-br from-white to-slate-50 border border-slate-100 shadow-2xl flex items-center justify-center p-12 overflow-hidden group">
                        <div className="absolute inset-0 opacity-[0.02] bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>

                        <div className="relative z-10 text-center">
                          <div className="mb-6 inline-block p-8 bg-maroon-700 rounded-3xl shadow-2xl shadow-maroon-200 rotate-3 transform group-hover:rotate-0 transition-transform duration-500">
                            {service.icon && (
                              <service.icon
                                size={80}
                                strokeWidth={1.5}
                                className="text-white"
                              />
                            )}
                          </div>
                          <div className="flex items-center justify-center gap-2 text-slate-400">
                            <Zap size={16} />
                            <span className="text-[10px] uppercase tracking-[0.3em] font-black">
                              Core Pillar
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Benefits Pills */}
                      <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl">
                        <h4 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6">
                          Strategic Benefits
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {service.benefits &&
                            service.benefits.map((benefit, bIdx) => (
                              <span
                                key={bIdx}
                                className="px-4 py-2 bg-slate-50 border border-slate-100 rounded-lg text-sm font-semibold text-slate-700"
                              >
                                {benefit}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SCROLLING CONTENT COLUMN */}
                  <div className="flex-1 w-full lg:w-2/3 space-y-12">
                    {/* Header */}
                    <div>
                      <div className="inline-flex items-center gap-2 text-maroon-700 font-bold uppercase tracking-widest text-xs mb-4">
                        <span className="h-px w-10 bg-maroon-700"></span>
                        {service.tagline}
                      </div>
                      <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-6">
                        {service.title}
                      </h2>
                      <p className="text-xl text-slate-600 leading-relaxed">
                        {service.heroDescription}
                      </p>
                    </div>

                    {/* Detailed Sections (The sub-services) */}
                    <div className="space-y-6">
                      <h3 className="text-2xl font-bold text-slate-900 mb-6">
                        Capabilities & Offerings
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        {service.sections &&
                          service.sections.map((sec, sIdx) => (
                            <div
                              key={sIdx}
                              className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group"
                            >
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-maroon-100 group-hover:bg-maroon-600 transition-colors"></div>
                              <h4 className="text-lg font-bold text-slate-900 mb-3 leading-tight">
                                {sec.title}
                              </h4>
                              <p className="text-slate-600 text-sm leading-relaxed">
                                {sec.content}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Process Timeline */}
                    {service.process && (
                      <div className="pt-6">
                        <h3 className="text-2xl font-bold text-slate-900 mb-8">
                          Our Approach
                        </h3>
                        <div className="flex flex-col md:flex-row gap-4 relative">
                          {/* Connecting Line (Desktop) */}
                          <div className="hidden md:block absolute top-6 left-6 right-6 h-0.5 bg-slate-100 z-0"></div>

                          {service.process.map((step, pIdx) => (
                            <div key={pIdx} className="flex-1 relative z-10">
                              <div className="flex flex-row md:flex-col items-center md:items-start gap-4 md:gap-0">
                                <div className="h-12 w-12 rounded-full bg-white border-4 border-slate-50 flex items-center justify-center text-maroon-700 font-black shadow-md md:mb-4 shrink-0">
                                  0{pIdx + 1}
                                </div>
                                <h5 className="font-bold text-slate-900 leading-tight">
                                  {step}
                                </h5>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* CTA Button */}
                    <div className="pt-8 border-t border-slate-100">
                      <Link
                        to={`/contact?service=${service.slug}`}
                        className="group inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-maroon-700 transition-all shadow-xl"
                      >
                        Enquire About This Service{" "}
                        <ArrowRight
                          size={18}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* --- BOTTOM CTA SECTION --- */}
      <section className="py-24 px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto rounded-[3rem] bg-gradient-to-br from-maroon-800 to-maroon-950 p-12 md:p-20 text-center relative overflow-hidden shadow-2xl"
        >
          <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>

          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
              Ready to Execute <br />{" "}
              <span className="text-maroon-300">Your Vision?</span>
            </h2>
            <p className="text-maroon-50 text-xl mb-12 max-w-2xl mx-auto font-medium">
              Connect with our consulting partners to build a tailored roadmap
              for your enterprise's digital and operational transformation.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-white text-maroon-900 px-12 py-8 rounded-2xl font-black text-xl hover:bg-slate-100 transition-all transform hover:-translate-y-1 hover:shadow-2xl"
              >
                Schedule a Consultation
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Services;
